import React, { useState, useEffect, RefObject } from 'react';
import styled from 'styled-components';

const Button = styled.button<{ $visible: boolean }>`
  position: fixed;
  /* Sits above the floating bottom nav dock (which occupies ~18–82px). */
  bottom: 96px;
  left: 50%;
  z-index: 900;
  display: flex;
  align-items: center;
  gap: 6px;
  /* Slim vertically (less height) while keeping the horizontal width comfortable. */
  padding: 5px 20px;
  /* [GLASSY 2026-08-31, user] "make it glassy green colour, not this much bold" — the old
     capsule was a near-opaque green gradient with a green glow, which read as a primary action.
     Now it is a true glass tint: translucent green over the blur, green-tinted hairline, and
     the readable green ink for the text instead of white. The glow is gone; a quiet drop
     shadow keeps it floating. */
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.32)' : 'rgba(26,127,100,0.28)')};
  border-radius: 24px;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 55%),
    ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.14)' : 'rgba(26,127,100,0.12)')};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  color: ${(p) => p.theme.greenInk};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translate(-50%, ${(p) => (p.$visible ? '0' : '16px')});
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
  transition: opacity 240ms cubic-bezier(0.4, 0, 0.2, 1), transform 240ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow:
      0 10px 26px rgba(0, 0, 0, 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 0.24);
  }

  i {
    font-size: 11px;
  }
`;

interface Props {
  scrollRef: RefObject<HTMLDivElement | null>;
  threshold?: number;
}

const GoToTop: React.FC<Props> = ({ scrollRef, threshold = 200 }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;

    // Depending on layout, the actual scroller may be the ref element OR the
    // window/document — listen to both and react to whichever moves.
    const currentScroll = () =>
      Math.max(
        el?.scrollTop ?? 0,
        window.scrollY || 0,
        document.documentElement?.scrollTop || 0
      );

    const handleScroll = () => setVisible(currentScroll() > threshold);

    el?.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // initialise in case we mount already scrolled

    return () => {
      el?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollRef, threshold]);

  const scrollToTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Button $visible={visible} onClick={scrollToTop}>
      Go to Top <i className="fas fa-chevron-up"></i>
    </Button>
  );
};

export default GoToTop;
